import React, { useEffect, useState, FunctionComponent, MutableRefObject } from 'react';

import { getCustomFieldKeys } from '@api/v4/resources/custom_fields';
import { getUniqueValues } from '@api/v4/resources/custom_field_values';
import { CustomFieldKeysResponseObject } from '@api/v4/resources/CustomFieldKeysTypes';
import { MultiselectDropdown, ListOption, ListOptionValue } from '@components/library/dropdown/index';
import { StandardSwitch } from '@components/library/switch';
import { BFLoader } from '@components/common/loader/main';
import { FilterType, SearchFilterOperator, SearchFilterOperators } from '@components/show_page/sections/section_search/SearchTypes';
import Label from '@components/library/labels/PrimaryLabel';
import { Locale } from '@components/common/language_menu/languagesMap';
import { getCustomFieldKeysOptions, syncCustomFields } from '@components/show_page/sections/advanced_filters/customFieldsHelper';

type ChangeFilters = (filterKey: string, filterValue: string) => void
interface PrioritizedCustomFieldFilterProps {
  filteredCustomFieldsStringified: string;
  loading: boolean;
  removeFilters: ChangeFilters;
  searchFilterOperators: SearchFilterOperators;
  selectedUGTLocale: Locale;
  updateFilters: ChangeFilters;
  updateSearchFilterOperator: (filterType: string, operatorUpdate: { [key: string]: SearchFilterOperator }) => void;
  advancedFiltersContainerRef?: MutableRefObject<HTMLElement>;
}

// TODO, use [key in...] syntax for type?
export interface CustomFieldValues {
  [key: string]: ListOptionValue[];
}

const createOptions = (values: ListOptionValue[] | undefined): ListOption[] => (
  values?.map((value) => ({ label: value?.toString() || '', value })) || []
);

export const PrioritizedCustomFieldFilter: FunctionComponent<PrioritizedCustomFieldFilterProps> = ({
  advancedFiltersContainerRef,
  filteredCustomFieldsStringified,
  loading,
  removeFilters,
  searchFilterOperators,
  selectedUGTLocale,
  updateFilters,
  updateSearchFilterOperator,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [prioritizedCustomFieldKeys, setPrioritizedCustomFieldKeys] = useState<CustomFieldKeysResponseObject[]>([]);
  const [prioritizedCustomFieldValues, setPrioritizedCustomFieldValues] = useState<CustomFieldValues | undefined>(undefined);
  const [selectedValues, setSelectedValues] = useState<CustomFieldValues | undefined>(undefined);

  const getNameForKey = (selectedKey: string): string => (
    prioritizedCustomFieldKeys.find((key) => selectedKey === key.id)?.attributes?.name || ''
  );

  const fetchCustomFieldKeysAsync = async (): Promise<void> => {
    const response = await getCustomFieldKeys(getCustomFieldKeysOptions(selectedUGTLocale));
    const prioritized = response.flatMap((key) => (key.attributes.prioritized ? key : []));
    setPrioritizedCustomFieldKeys(prioritized);
    setIsLoading(false);
  };

  const fetchCustomFieldValuesAsync = async (customFieldKeyId: string): Promise<void> => {
    if (!prioritizedCustomFieldValues?.[customFieldKeyId]) {
      // values are fetched as needed when a user selects the custom field key dropdown
      // fetch values if not cached in state already
      try {
        const response = await getUniqueValues(customFieldKeyId);
        setPrioritizedCustomFieldValues((prevState) => ({
          ...prevState,
          [customFieldKeyId]: response.custom_field_values,
        }));
      } catch {
        // TODO catch something?
      }
    }
  };

  const handleChange = (
    selectedOptionValues: ListOptionValue[],
    isAdded: boolean,
    updatedValue: ListOptionValue,
    selectedKey: string,
  ): void => {
    setSelectedValues((prevState) => {
      if (isAdded) {
        return {
          ...prevState,
          [selectedKey]: [...selectedOptionValues]
        };
      }

      const updatedState = { ...prevState };
      const updatedValues = updatedState[selectedKey].filter((values) => values !== updatedValue);
      if (!updatedValues.length) {
        delete updatedState[selectedKey];
      } else {
        updatedState[selectedKey] = updatedValues;
      }
      return updatedState;
    });

    if (isAdded) {
      updateFilters(getNameForKey(selectedKey), updatedValue as string);
    } else {
      removeFilters(getNameForKey(selectedKey), updatedValue as string);
    }
  };

  const handleSwitchChange = (customFieldName: string): void => {
    const currentOperator = searchFilterOperators.custom_fields?.[customFieldName];
    const updatedOperator = currentOperator === SearchFilterOperator.AND
      ? SearchFilterOperator.OR
      : SearchFilterOperator.AND;
    updateSearchFilterOperator(FilterType.CustomFields, { [customFieldName]: updatedOperator });
  };

  useEffect(() => {
    syncCustomFields(filteredCustomFieldsStringified, prioritizedCustomFieldKeys, setSelectedValues);
  }, [filteredCustomFieldsStringified, prioritizedCustomFieldKeys]);

  useEffect(() => {
    fetchCustomFieldKeysAsync();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isLoading && prioritizedCustomFieldKeys.length === 0) {
    return null;
  }

  return (
    <div className="filter-section prioritized-custom-fields">
      {isLoading ? (
        <BFLoader />
      ) : prioritizedCustomFieldKeys.map((key) => {
        const listOptionValues = selectedValues?.[key.id];
        const hasSelectedValues = (listOptionValues && listOptionValues.length > 1) || false;
        return (
          <div
            key={key.id}
            className="prioritized-custom-fields__row"
          >
            <div className="prioritized-custom-fields__row--label-container">
              <Label>{key.attributes.name}</Label>
              {BFG.hasFeature('rollout_multi_value_custom_fields') && hasSelectedValues && (
              <div className="any-all-switch">
                <h4 className="any-all-copy">{bfTranslate('Any')}</h4>
                <StandardSwitch
                  isChecked={searchFilterOperators.custom_fields?.[key.attributes.name] === SearchFilterOperator.AND || false}
                  isDisabled={loading}
                  name={`enable all (inclusive search) for ${key.attributes.name} key`}
                  onChange={(): void => handleSwitchChange(key.attributes.name)}
                />
                <h4 className="any-all-copy">{bfTranslate('All')}</h4>
              </div>
              )}
            </div>
            <div className="prioritized-custom-fields__row--dropdown-container">
              <MultiselectDropdown
                className="custom-field-dropdown keys"
                isLoading={!prioritizedCustomFieldValues?.[key.id]}
                onChange={(selectedOptions, isAdded, updatedValue): void => {
                  handleChange(selectedOptions, isAdded || false, updatedValue, key.id);
                }}
                onOpen={(): Promise<void> => fetchCustomFieldValuesAsync(key.id)}
                options={createOptions(prioritizedCustomFieldValues?.[key.id])}
                overflowParentRef={advancedFiltersContainerRef}
                placeholder={`${bfTranslate("Select")} ${key.attributes.name}`}
                searchable
                values={(!!selectedValues?.[key.id] && createOptions(selectedValues[key.id])) || undefined}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};
